import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/shadcn/ContextMenu/ContextMenuPrimitives";
import { searchContactsQueryOptions } from "@/lib/data/react-query/contacts/contact";
import type { Contact } from "@/lib/types";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Mail,
  Phone,
  PlusCircle,
  Trash2,
  MessageSquare,
  UserPen,
  UserRound,
} from "lucide-react";
import type { ReactNode } from "react";

interface ContactSearchableContextMenuSubmenuProps {
  addLabel: string;
  matterId: string;
  isRequired?: boolean;
  isContactRequired?: (contact: Contact) => boolean;
  onAddContact: () => void;
  onChangeContact?: (contact: Contact) => void;
  onDeleteContact: (contact: Contact) => void;
  onEmailContact?: (contact: Contact) => void;
  onEditContact?: (contact: Contact) => void;
  onTextContact?: (contact: Contact) => void;
  selectedContactIds?: string[];
  selectionMode?: "multiple" | "single";
  title: ReactNode;
}

/** A selected-contact submenu with add, change, and delete actions. */
export const ContactSearchableContextMenuSubmenu = ({
  addLabel,
  matterId,
  isRequired = false,
  isContactRequired,
  onAddContact,
  onChangeContact,
  onDeleteContact,
  onEmailContact,
  onEditContact,
  onTextContact,
  selectedContactIds = [],
  selectionMode = "single",
  title,
}: ContactSearchableContextMenuSubmenuProps) => {
  const { data: selectedContactsData } = useQuery({
    ...searchContactsQueryOptions({
      contactIds: selectedContactIds,
      matterIds: [matterId],
    }),
    enabled: selectedContactIds.length > 0,
  });
  const selectedContactsById = new Map(
    (selectedContactsData?.data ?? []).map((contact) => [contact.id, contact]),
  );
  const selectedContacts = selectedContactIds.flatMap((id) => {
    const contact = selectedContactsById.get(id);
    return contact ? [contact] : [];
  });
  const canAdd =
    selectionMode === "multiple" || selectedContactIds.length === 0;

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>{title}</ContextMenuSubTrigger>
      <ContextMenuSubContent className="min-w-56">
        {selectedContacts.map((contact) => (
          <ContextMenuSub key={contact.id}>
            <ContextMenuSubTrigger className="h-auto py-1">
              <ContactMenuDetails contact={contact} />
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {selectionMode === "single" ? (
                <ContextMenuItem onSelect={() => onChangeContact?.(contact)}>
                  <ArrowLeftRight />
                  Change
                </ContextMenuItem>
              ) : null}
              {onEditContact ? (
                <ContextMenuItem
                  onSelect={() => {
                    onEditContact(contact);
                  }}
                >
                  <UserPen />
                  Edit contact
                </ContextMenuItem>
              ) : null}
              {onEmailContact && hasEmailAddress(contact) ? (
                <ContextMenuItem
                  onSelect={() => {
                    onEmailContact(contact);
                  }}
                >
                  <Mail />
                  Email
                </ContextMenuItem>
              ) : null}
              {onTextContact && hasPhoneNumber(contact) ? (
                <ContextMenuItem
                  onSelect={() => {
                    onTextContact(contact);
                  }}
                >
                  <MessageSquare />
                  Text
                </ContextMenuItem>
              ) : null}
              {!isRequired && !isContactRequired?.(contact) ? (
                <ContextMenuItem
                  onSelect={() => {
                    onDeleteContact(contact);
                  }}
                >
                  <Trash2 />
                  Remove
                </ContextMenuItem>
              ) : null}
            </ContextMenuSubContent>
          </ContextMenuSub>
        ))}
        {canAdd ? (
          <ContextMenuItem onSelect={onAddContact}>
            <PlusCircle />
            {addLabel}
          </ContextMenuItem>
        ) : null}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
};

const hasEmailAddress = (contact: Contact) =>
  contact.emails.some((email) => Boolean(email.address?.trim()));

const hasPhoneNumber = (contact: Contact) =>
  contact.phones.some((phone) => Boolean(phone.number.trim()));

const ContactMenuDetails = ({ contact }: { contact: Contact }) => {
  const email =
    contact.emails.find((contactEmail) => contactEmail.isPrimary)?.address ??
    contact.emails.at(0)?.address;
  const phone =
    contact.phones.find((contactPhone) => contactPhone.isPrimary)?.number ??
    contact.phones.at(0)?.number;
  const phoneLabel = phone ? formatLocalOrIntlPhoneNumber(phone) || phone : "";

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1">
      <span className="flex min-w-0 items-center gap-1">
        <UserRound className="h-3 w-3 shrink-0" />
        <span className="truncate font-medium">
          {formatContactsName(contact) || "Unnamed contact"}
        </span>
      </span>
      {email || phoneLabel ? (
        <div className="text-muted-foreground flex min-w-0 flex-col gap-0.5 text-xs">
          {email ? (
            <span className="flex min-w-0 items-center gap-1">
              <Mail className="h-3 w-3 shrink-0" />
              <span className="truncate">{email}</span>
            </span>
          ) : null}
          {phoneLabel ? (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Phone className="h-3 w-3 shrink-0" />
              {phoneLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
